import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { IoCloseOutline, IoChevronBackOutline } from "react-icons/io5";
import { FaFacebookF, FaWhatsapp, FaRegCopy } from "react-icons/fa";

const BlogDetail = () => {
  const { id } = useParams();
  const [blog, setBlog] = useState(null);
  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [categoryCounts, setCategoryCounts] = useState({});

  useEffect(() => {
    fetchBlogDetail();
    fetchAllBlogs();
  }, [id]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) return "";
    return imagePath.startsWith("http")
      ? imagePath
      : `https://dev.mithahomes.com/backend${imagePath}`;
  };

  const allImages = useMemo(() => {
    if (!blog) return [];
    const mainImg = blog.image ? [blog.image] : [];
    let extraImgs = [];
    try {
      extraImgs =
        typeof blog.images === "string"
          ? JSON.parse(blog.images)
          : blog.images || [];
    } catch (e) {
      extraImgs = [];
    }
    return [...mainImg, ...extraImgs].filter(Boolean);
  }, [blog]);

  const fetchBlogDetail = async () => {
    try {
      setLoading(true);
      const response = await fetch(
        `https://dev.mithahomes.com/backend/api/blogs/get/${id}`,
      );
      const data = await response.json();
      setBlog(data.blog);
    } catch (error) {
      toast.error("Error loading blog details");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBlogs = async () => {
    try {
      const response = await fetch(
        "https://dev.mithahomes.com/backend/api/blogs/all",
      );
      const data = await response.json();
      const blogs = data.blogs || [];
      setAllBlogs(blogs);
      const counts = blogs.reduce((acc, curr) => {
        acc[curr.category] = (acc[curr.category] || 0) + 1;
        return acc;
      }, {});
      setCategoryCounts(counts);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success("Link copied to clipboard!");
  };

  const openImageModal = (imageUrl, index) => {
    setSelectedImage(imageUrl);
    setSelectedImageIndex(index);
    setShowModal(true);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setShowModal(false);
    document.body.style.overflow = "auto";
  };

  if (loading) return <div style={styles.loader}>Loading Article...</div>;
  if (!blog) return <div style={styles.loader}>Blog not found.</div>;

  const shareUrl = window.location.href;
  const hasMultipleImages = allImages.length > 1;

  return (
    <div style={styles.container}>
      <ToastContainer position="bottom-center" />

      <style>{`
        .main-wrapper {
          display: flex;
          flex-direction: row;
          gap: 30px;
          max-width: 1250px;
          margin: 0 auto;
          padding: 20px;
        }
        
        .content-body img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
        
        .content-body p {
          line-height: 1.8;
          color: #334155;
          margin-bottom: 1.5rem;
        }
        
        @media (max-width: 992px) {
          .main-wrapper {
            flex-direction: column;
          }
          .sidebar {
            width: 100% !important;
          }
        }
        
        @media (max-width: 768px) {
          .blog-title { 
            font-size: 28px !important; 
          }
          
          .image-grid-responsive {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 10px !important;
          }
        }
        
        @media (max-width: 480px) {
          .blog-title { 
            font-size: 24px !important; 
          }
          
          .image-grid-responsive {
            grid-template-columns: 1fr !important;
          }
          
          .single-image-container {
            max-height: 350px !important;
          }
        }
        
        .full-grid-img:hover {
          transform: scale(1.02);
          transition: transform 0.3s ease;
        }
        
        .single-image-container:hover img {
          transform: scale(1.01);
          transition: transform 0.3s ease;
        }
      `}</style>

      <div className="main-wrapper">
        {/* Main Content Area */}
        <div style={styles.leftColumn}>
          <Link to="/blog" style={styles.backBtn}>
            <IoChevronBackOutline /> Back to Articles
          </Link>

          <div style={styles.card}>
            <span style={styles.badge}>{blog.category}</span>
            <h1 className="blog-title" style={styles.mainTitle}>
              {blog.title}
            </h1>
            <p style={styles.dateText}>
              Published on {new Date(blog.created_at).toLocaleDateString()}
            </p>

            {/* Image Display Section - Shows ALL Images Clearly */}
            {allImages.length > 0 && (
              <div style={styles.imageSection}>
                {hasMultipleImages ? (
                  // Responsive Grid for Multiple Images
                  <div
                    className="image-grid-responsive"
                    style={styles.fullImageGrid}
                  >
                    {allImages.map((img, index) => (
                      <div
                        key={index}
                        style={styles.fullGridItem}
                        onClick={() => openImageModal(getImageUrl(img), index)}
                        className="full-grid-img"
                      >
                        <img
                          src={getImageUrl(img)}
                          alt={`${blog.title} - Image ${index + 1}`}
                          style={styles.fullGridImg}
                          loading="lazy"
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  // Single Image Full Width
                  <div
                    className="single-image-container"
                    style={styles.singleImageContainer}
                    onClick={() => openImageModal(getImageUrl(allImages[0]), 0)}
                  >
                    <img
                      src={getImageUrl(allImages[0])}
                      alt={blog.title}
                      style={styles.singleImage}
                      loading="lazy"
                    />
                  </div>
                )}
              </div>
            )}

            {/* Blog Content */}
            <div
              className="content-body"
              style={styles.articleBody}
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />

            {/* Social Sharing */}
            <div style={styles.shareSection}>
              <h4 style={{ marginBottom: "15px" }}>Share this post</h4>
              <div style={styles.socialRow}>
                <button
                  onClick={() => window.open(`https://wa.me/?text=${shareUrl}`)}
                  style={{ ...styles.sBtn, background: "#25D366" }}
                  aria-label="Share on WhatsApp"
                >
                  <FaWhatsapp />
                </button>
                <button
                  onClick={() =>
                    window.open(
                      `https://facebook.com/sharer/sharer.php?u=${shareUrl}`,
                    )
                  }
                  style={{ ...styles.sBtn, background: "#1877F2" }}
                  aria-label="Share on Facebook"
                >
                  <FaFacebookF />
                </button>
                <button
                  onClick={handleCopyLink}
                  style={{ ...styles.sBtn, background: "#64748b" }}
                  aria-label="Copy link"
                >
                  <FaRegCopy />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="sidebar" style={styles.rightColumn}>
          <div style={styles.sidebarCard}>
            <h3 style={styles.sideHeading}>Categories</h3>
            {Object.keys(categoryCounts).map((cat) => (
              <Link key={cat} to={`/category/${cat}`} style={styles.catLink}>
                <span>{cat}</span>
                <span style={styles.catCount}>{categoryCounts[cat]}</span>
              </Link>
            ))}
          </div>

          <div style={{ ...styles.sidebarCard, marginTop: "20px" }}>
            <h3 style={styles.sideHeading}>Recent Posts</h3>
            {allBlogs.slice(0, 4).map((post) => (
              <Link
                key={post.id}
                to={`/blog/${post.id}`}
                style={styles.recentPost}
              >
                <img
                  src={getImageUrl(post.image)}
                  alt={post.title}
                  style={styles.recentImg}
                  loading="lazy"
                />
                <div style={styles.recentInfo}>
                  <p style={styles.recentTitle}>{post.title}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Full Screen Image Modal */}
      {showModal && (
        <div style={styles.modal} onClick={closeModal}>
          <button
            style={styles.modalClose}
            onClick={closeModal}
            aria-label="Close image"
          >
            <IoCloseOutline size={35} />
          </button>
          <div style={styles.modalImageContainer}>
            <img
              src={selectedImage}
              alt="Full view"
              style={styles.modalImg}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    backgroundColor: "#f8fafc",
    minHeight: "100vh",
    fontFamily: "system-ui, -apple-system, sans-serif",
  },

  loader: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "100px",
    fontSize: "18px",
    color: "#64748b",
    minHeight: "60vh",
  },

  leftColumn: {
    flex: 2,
    minWidth: 0,
  },

  rightColumn: {
    flex: 1,
    width: "320px",
    maxWidth: "100%",
  },

  card: {
    backgroundColor: "#fff",
    padding: "30px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    marginBottom: "30px",
  },

  backBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    textDecoration: "none",
    color: "#5eba49",
    fontWeight: "600",
    marginBottom: "25px",
    fontSize: "16px",
    padding: "8px 16px",
    borderRadius: "8px",
    backgroundColor: "#f0fdf4",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "#dcfce7",
    },
  },

  badge: {
    backgroundColor: "#f0fdf4",
    color: "#166534",
    padding: "6px 16px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "700",
    textTransform: "uppercase",
    display: "inline-block",
    marginBottom: "10px",
  },

  mainTitle: {
    fontSize: "36px",
    color: "#0f172a",
    margin: "15px 0 15px 0",
    lineHeight: 1.3,
    fontWeight: "700",
  },

  dateText: {
    color: "#64748b",
    fontSize: "15px",
    marginBottom: "30px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },

  imageSection: {
    marginBottom: "40px",
  },

  // Single Image Styles (Full Width)
  singleImageContainer: {
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    cursor: "pointer",
    maxHeight: "650px",
    backgroundColor: "#f9fafb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "10px",
  },

  singleImage: {
    width: "100%",
    height: "auto",
    maxHeight: "600px",
    objectFit: "contain",
    borderRadius: "8px",
    display: "block",
  },

  // Grid for Multiple Images
  fullImageGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: "15px",
    borderRadius: "12px",
    overflow: "hidden",
  },

  fullGridItem: {
    position: "relative",
    aspectRatio: "1/1",
    overflow: "hidden",
    cursor: "pointer",
    borderRadius: "10px",
    backgroundColor: "#f9fafb",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "8px",
  },

  fullGridImg: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    borderRadius: "8px",
    display: "block",
  },

  // Article Content
  articleBody: {
    fontSize: "18px",
    color: "#334155",
    lineHeight: "1.8",
    marginTop: "30px",
  },

  // Social Share
  shareSection: {
    marginTop: "50px",
    borderTop: "1px solid #e2e8f0",
    paddingTop: "25px",
  },

  socialRow: {
    display: "flex",
    gap: "12px",
    flexWrap: "wrap",
  },

  sBtn: {
    width: "48px",
    height: "48px",
    borderRadius: "50%",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    transition: "all 0.3s ease",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
    ":hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
    },
    ":active": {
      transform: "translateY(0)",
    },
  },

  // Sidebar Styles
  sidebarCard: {
    backgroundColor: "#fff",
    padding: "25px",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    marginBottom: "20px",
  },

  sideHeading: {
    fontSize: "20px",
    marginBottom: "20px",
    borderBottom: "3px solid #5eba49",
    display: "inline-block",
    paddingBottom: "8px",
    color: "#0f172a",
    fontWeight: "600",
  },

  catLink: {
    display: "flex",
    justifyContent: "space-between",
    textDecoration: "none",
    color: "#475569",
    padding: "12px 0",
    borderBottom: "1px solid #f1f5f9",
    transition: "all 0.3s ease",
    fontSize: "15px",
    ":hover": {
      color: "#5eba49",
      paddingLeft: "8px",
    },
  },

  catCount: {
    backgroundColor: "#f1f5f9",
    padding: "4px 10px",
    borderRadius: "20px",
    fontSize: "13px",
    fontWeight: "600",
    color: "#64748b",
  },

  // Recent Posts
  recentPost: {
    display: "flex",
    gap: "15px",
    textDecoration: "none",
    color: "inherit",
    marginBottom: "18px",
    padding: "8px",
    borderRadius: "8px",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "#f8fafc",
      transform: "translateX(5px)",
    },
  },

  recentImg: {
    width: "70px",
    height: "70px",
    borderRadius: "8px",
    objectFit: "cover",
    minWidth: "70px",
  },

  recentTitle: {
    fontSize: "15px",
    fontWeight: "600",
    lineHeight: "1.4",
    color: "#334155",
    margin: 0,
  },

  // Modal Styles
  modal: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.95)",
    zIndex: 9999,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "20px",
    cursor: "pointer",
  },

  modalImageContainer: {
    maxWidth: "95vw",
    maxHeight: "90vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  modalImg: {
    maxWidth: "100%",
    maxHeight: "90vh",
    borderRadius: "8px",
    objectFit: "contain",
    cursor: "default",
  },

  modalClose: {
    position: "absolute",
    top: "25px",
    right: "25px",
    background: "rgba(255,255,255,0.1)",
    border: "none",
    color: "#fff",
    cursor: "pointer",
    width: "50px",
    height: "50px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    ":hover": {
      backgroundColor: "rgba(255,255,255,0.2)",
      transform: "rotate(90deg)",
    },
  },
};

export default BlogDetail;
